import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogClose,
  DialogPopup,
  DialogTitle,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  Text,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

import type { LightboxMediaItem } from "./media-lightbox";
import { ResolvedMediaTile } from "./media-tiles";

const DISMISS_SHARE = 0.18;
const DISMISS_SPEED = 0.6;
const INTENT_SLOP = 10;
const LOADED_NEIGHBOURS = 1;
const FILM_TILE = 36;
const FILM_WINDOW = 12;

export function PhoneMediaLightbox({
  media,
  index,
  onIndexChange,
  open,
  onOpenChange,
  title,
  renderItem,
  onRemove,
  onDownload,
  onDownloadAll,
  downloadingAll,
}: Omittable<{
  media: LightboxMediaItem[];
  index: number;
  onIndexChange: (index: number) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  renderItem: (item: LightboxMediaItem) => React.ReactNode;
  onRemove?: () => void;
  onDownload: () => void;
  onDownloadAll: () => void;
  downloadingAll: boolean;
}>) {
  const count = media.length;
  const scroller = useRef<HTMLDivElement | null>(null);
  // The popup mounts its body a render after `open`, so the open scroll waits for the scroller.
  const [scrollerNode, setScrollerNode] = useState<HTMLDivElement | null>(null);
  const attachScroller = useCallback((node: HTMLDivElement | null) => {
    scroller.current = node;
    setScrollerNode(node);
  }, []);
  const press = useRef<{ x: number; y: number; at: number; pulling: boolean } | null>(null);
  const tapMoved = useRef(false);
  const [pull, setPull] = useState(0);
  const [settling, setSettling] = useState(false);
  const [chrome, setChrome] = useState(true);

  useEffect(() => {
    if (open) setChrome(true);
  }, [open]);

  useEffect(() => {
    const el = scrollerNode;
    if (!open || !el) return;
    el.scrollLeft = index * el.clientWidth;
    // The index is read from the scroll; following it here would fight the
    // swipe that set it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, count, scrollerNode]);

  const film = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const tile = film.current?.children[index];
    if (tile instanceof HTMLElement) {
      tile.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [index]);

  const jumpTo = (i: number) => {
    const el = scroller.current;
    if (el) el.scrollTo({ behavior: "smooth", left: i * el.clientWidth });
  };

  const onScroll = () => {
    const el = scroller.current;
    if (!el || el.clientWidth === 0) return;
    const next = Math.round(el.scrollLeft / el.clientWidth);
    if (next !== index && next >= 0 && next < count) onIndexChange(next);
  };

  const height = typeof window === "undefined" ? 800 : window.innerHeight;
  const bar = `absolute inset-x-0 z-10 bg-black/45 backdrop-blur-xl transition-opacity duration-200 motion-reduce:transition-none ${
    chrome && pull === 0 ? "opacity-100" : "pointer-events-none opacity-0"
  }`;
  const progress = Math.min(pull / height, 1);
  const popup = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    popup.current?.style.setProperty("--scrim", `${Math.max(1 - progress * 1.6, 0) * 100}%`);
    scroller.current?.style.setProperty("--pull", `${pull}px`);
    scroller.current?.style.setProperty("--shrink", `${1 - progress * 0.25}`);
  });

  const release = (y: number) => {
    const start = press.current;
    press.current = null;
    if (!start?.pulling) return;
    const distance = Math.max(y - start.y, 0);
    const speed = distance / Math.max(performance.now() - start.at, 1);
    setSettling(true);
    if (distance > height * DISMISS_SHARE || speed > DISMISS_SPEED) {
      onOpenChange(false);
      return;
    }
    setPull(0);
  };

  return (
    <Dialog
      onOpenChange={onOpenChange}
      onOpenChangeComplete={(next) => {
        if (!next) setPull(0);
      }}
      open={open}
      responsive={false}
    >
      {/* `dark` scopes the dark tokens: photos read on black in either theme.
          The black thins as the photo is pulled down, showing the screen
          the viewer will close back to. */}
      <DialogPopup
        className="dark bg-black/(--scrim) [--scrim:100%]"
        ref={popup}
        showCloseButton={false}
        size="screen"
      >
        <Box
          align="center"
          className={`${bar} top-0 pt-[max(env(safe-area-inset-top),--spacing(2))]`}
          columns={3}
          paddingBottom="xs"
          paddingX="sm"
        >
          <Box align="start">
            <DialogClose
              aria-label="Close"
              render={<Button size="icon" variant="ghost" />}
            >
              <Icon name="x" size="md" />
            </DialogClose>
          </Box>
          <Box align="center" direction="column">
            <DialogTitle className="max-w-full truncate text-sm font-semibold">
              {title ?? "Photos"}
            </DialogTitle>
            {count > 1 ? (
              <Text className="tabular-nums" size="xs" variant="muted">
                {`${index + 1} of ${count}`}
              </Text>
            ) : null}
          </Box>
          <Box />
        </Box>
        <Box
          className={`snap-x snap-mandatory [scrollbar-width:none] [touch-action:pan-x] [--pull:0px] [--shrink:1] [transform:translateY(var(--pull))_scale(var(--shrink))] ${settling ? "transition-transform duration-300 ease-[cubic-bezier(0.2,0.9,0.3,1)]" : ""}`}
          direction="row"
          fill
          onClick={() => {
            if (!tapMoved.current) setChrome((current) => !current);
          }}
          onPointerCancel={() => {
            press.current = null;
            setPull(0);
          }}
          onPointerDown={(event) => {
            press.current = { at: performance.now(), pulling: false, x: event.clientX, y: event.clientY };
            tapMoved.current = false;
            setSettling(false);
          }}
          onPointerMove={(event) => {
            const start = press.current;
            if (!start) return;
            const dx = event.clientX - start.x;
            const dy = event.clientY - start.y;
            if (!start.pulling) {
              if (dy < INTENT_SLOP || Math.abs(dx) > dy) return;
              start.pulling = true;
              tapMoved.current = true;
              event.currentTarget.setPointerCapture(event.pointerId);
            }
            setPull(Math.max(dy, 0));
          }}
          onPointerUp={(event) => release(event.clientY)}
          onScroll={onScroll}
          overflowX="auto"
          ref={attachScroller}
        >
          {media.map((item, i) => (
            <Box
              align="center"
              className="snap-center snap-always"
              fill="height"
              justify="center"
              key={item.id}
              shrink={false}
              width="100%"
            >
              {Math.abs(i - index) <= LOADED_NEIGHBOURS ? renderItem(item) : null}
            </Box>
          ))}
        </Box>
        <Box
          align="center"
          className={`${bar} bottom-0 pb-[max(env(safe-area-inset-bottom),--spacing(2))]`}
          direction="row"
          justify="between"
          paddingTop="xs"
          paddingX="sm"
        >
          <Menu>
            <MenuTrigger render={<Button aria-label="Download" size="icon-lg" variant="ghost" />}>
              <Icon name="download" size="md" />
            </MenuTrigger>
            <MenuPopup align="start" side="top">
              <MenuItem onClick={onDownload}>
                <Icon name="download" />
                Download
              </MenuItem>
              <MenuItem disabled={count === 0 || downloadingAll} onClick={onDownloadAll}>
                <Icon name="download" />
                {downloadingAll ? "Preparing zip..." : "Download all"}
              </MenuItem>
            </MenuPopup>
          </Menu>
          {count > 1 ? (
            <Box
              align="center"
              className="[scrollbar-width:none]"
              direction="row"
              gap="2xs"
              grow
              minWidth={0}
              overflowX="auto"
              paddingX="xs"
              paddingY="2xs"
              ref={film}
            >
              {media.map((item, i) => (
                <Box
                  className={
                    i === index
                      ? "ring-2 ring-white"
                      : "opacity-60 transition-opacity motion-reduce:transition-none"
                  }
                  height={FILM_TILE}
                  key={item.id}
                  rounded="md"
                  shrink={false}
                  width={FILM_TILE}
                >
                  {Math.abs(i - index) <= FILM_WINDOW ? (
                    <ResolvedMediaTile
                      label={`Go to photo ${i + 1} of ${count}`}
                      onClick={() => jumpTo(i)}
                      photoId={item.id}
                      size={FILM_TILE}
                    />
                  ) : null}
                </Box>
              ))}
            </Box>
          ) : null}
          {onRemove ? (
            <Button
              aria-label="Delete photo"
              color="destructive"
              onClick={onRemove}
              size="icon-lg"
              variant="ghost"
            >
              <Icon name="trash" size="md" />
            </Button>
          ) : null}
        </Box>
      </DialogPopup>
    </Dialog>
  );
}
